/**
 * The inspector's hover popover and keyboard shortcuts, shared by all three inspector surfaces:
 * the hydrated app (InspectorOverlay.tsx), non-hydrating SSR routes (build-static-inspector-script.ts)
 * and web-static pages (build-static-copy-inspector-script.ts).
 *
 * Hovering a marked string opens a small card with its id (or source reference on web-static
 * pages), the English original, and buttons to copy either. While a string is hovered:
 *   I  copies its id
 *   T  copies its visible text
 *   E  switches the page between the translation and English (works without hovering too)
 * Plain letter keys, no modifiers: Alt+Click turned out hard to discover (and on a Mac the key is
 * labelled Option), and a bare click still always reaches the link or button underneath.
 *
 * `installInspectorPopover` must stay self-contained (no imports, no references to anything else
 * in this module): the two static surfaces ship it as an inline script via `String(fn)`. Keep it to
 * syntax every current browser runs natively so the bundler never injects a helper into its body.
 * INSPECTOR_POPOVER_SOURCE prepends a no-op `__name` in case esbuild's keepNames wraps it anyway.
 */

export interface InspectorPopoverConfig {
  /** CSS selector matching a marked string. */
  selector: string;
  /** What "copy id" copies, and the popover's first row. */
  getId: (el: Element) => string;
  /** Label for that row: "Id" for catalogue messages, "Source" for web-static file references. */
  idLabel: string;
  /** English original: a string, null when there is none, undefined while still loading. */
  getEnglish: (el: Element) => string | null | undefined;
  /** Page locale. The English row is hidden on English pages. */
  locale: string;
  /**
   * Switches the whole page between translation and English. Omit to use the built-in DOM swap
   * (static surfaces, which can't re-render): it replaces the text of markers that hold plain
   * text only, and leaves markers with links or live values inside untouched.
   */
  toggleEnglish?: () => void;
  isEnglishOn?: () => boolean;
  /** Called after every copy, and once on install with the last copy from this tab session. */
  onCopy?: (text: string) => void;
  /** Called whenever the built-in DOM swap turns English on or off. */
  onEnglishChange?: (on: boolean) => void;
}

export interface InspectorPopoverHandle {
  /** Same as pressing E: for a host's own "Show English" button. */
  toggleEnglish: () => void;
  dispose: () => void;
}

export function installInspectorPopover(config: InspectorPopoverConfig): InspectorPopoverHandle {
  const LAST_COPIED_KEY = 'i18n-inspector-last-copied';
  const doc = document;

  const style = doc.createElement('style');
  // Design-system anatomy in raw CSS (the static surfaces have no Tailwind build for this): a flat
  // Card (surface, card hairline, container radius, md shadow), eyebrow labels, secondary pill
  // buttons, and forest + a check for the copy confirmation (good always carries a glyph).
  style.textContent = [
    '#i18n-inspect-popover{position:fixed;z-index:2147483001;box-sizing:border-box;width:max-content;max-width:min(360px,calc(100vw - 16px));min-width:240px;padding:12px 14px;font-family:inherit;font-size:14px;font-weight:400;line-height:1.45;color:var(--jc-ink);background:var(--jc-surface);border:1px solid var(--jc-card-border);border-radius:var(--jc-radius-container);box-shadow:var(--jc-shadow-md);text-align:left;white-space:normal;letter-spacing:normal;text-transform:none;font-style:normal}',
    '#i18n-inspect-popover[hidden]{display:none}',
    '#i18n-inspect-popover .i18n-pop-label{display:block;margin:10px 0 2px;font-size:12px;font-weight:600;line-height:1.3;letter-spacing:.06em;text-transform:uppercase;color:var(--jc-ink-secondary)}',
    '#i18n-inspect-popover .i18n-pop-label:first-child{margin-top:0}',
    '#i18n-inspect-popover .i18n-pop-id{font:400 13px/1.4 ui-monospace,SFMono-Regular,Menlo,monospace;word-break:break-all;white-space:pre-wrap}',
    '#i18n-inspect-popover .i18n-pop-en{padding:6px 8px;border-radius:var(--jc-radius-info);background:var(--jc-elevated);white-space:pre-wrap;word-break:break-word}',
    '#i18n-inspect-popover .i18n-pop-muted{color:var(--jc-ink-secondary)}',
    '#i18n-inspect-popover .i18n-pop-actions{display:flex;flex-wrap:wrap;gap:6px;margin-top:12px}',
    '#i18n-inspect-popover button{display:inline-flex;align-items:center;gap:6px;height:32px;padding:0 12px;font-family:inherit;font-size:13px;font-weight:600;line-height:1;color:var(--jc-ink);background:var(--jc-surface);border:0;box-shadow:inset 0 0 0 1px var(--jc-border-control);border-radius:var(--jc-radius-control);cursor:pointer}',
    '#i18n-inspect-popover button:hover{background:var(--jc-elevated)}',
    '#i18n-inspect-popover button:focus-visible{outline:2px solid var(--jc-focus);outline-offset:2px}',
    '#i18n-inspect-popover button[aria-pressed="true"]{background:var(--jc-selected);color:var(--jc-on-selected);box-shadow:none}',
    '#i18n-inspect-popover kbd{display:inline-flex;align-items:center;justify-content:center;min-width:18px;height:18px;padding:0 4px;font:600 11px/1 ui-monospace,SFMono-Regular,Menlo,monospace;color:var(--jc-ink-secondary);background:var(--jc-elevated);border-radius:4px}',
    '#i18n-inspect-popover button[aria-pressed="true"] kbd{color:var(--jc-on-selected);background:transparent;box-shadow:inset 0 0 0 1px currentColor}',
    '#i18n-inspect-popover .i18n-pop-status{margin-top:8px;font-size:13px;font-weight:600;color:var(--jc-selected);word-break:break-word}',
    '#i18n-inspect-popover .i18n-pop-status:empty{display:none}',
    '.i18n-inspect-flash{animation:i18n-inspect-flash 1.6s ease-out}',
    '@keyframes i18n-inspect-flash{0%{background:color-mix(in srgb,var(--jc-selected) 30%,transparent)}100%{background:transparent}}',
    '@media (prefers-reduced-motion:reduce){.i18n-inspect-flash{animation:none;outline:2px solid var(--jc-selected)}}',
  ].join('\n');
  doc.head.appendChild(style);

  const pop = doc.createElement('div');
  pop.id = 'i18n-inspect-popover';
  pop.hidden = true;
  pop.setAttribute('role', 'tooltip');
  // Tells the web-static matcher (and anything else walking the page) this is inspector UI.
  pop.setAttribute('data-i18n-static-ui', '');
  pop.setAttribute('data-i18n-inspector-ui', '');
  doc.body.appendChild(pop);

  let current: Element | null = null;
  let showTimer: ReturnType<typeof setTimeout> | undefined;
  let hideTimer: ReturnType<typeof setTimeout> | undefined;
  let statusTimer: ReturnType<typeof setTimeout> | undefined;
  let domEnglishOn = false;
  const originals = new Map<Element, string>();

  const englishOn = (): boolean => (config.isEnglishOn ? config.isEnglishOn() : domEnglishOn);

  const el = (tag: string, className: string, text?: string): HTMLElement => {
    const node = doc.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  const button = (key: string, label: string, onClick: () => void): HTMLElement => {
    const b = el('button', '');
    b.setAttribute('type', 'button');
    b.appendChild(el('kbd', '', key));
    b.appendChild(doc.createTextNode(label));
    b.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    });
    return b;
  };

  const visibleText = (marker: Element): string =>
    (marker.textContent || '').replace(/[\s  ]+/g, ' ').trim();

  const setStatus = (text: string): void => {
    const status = pop.querySelector('.i18n-pop-status');
    if (!status) return;
    status.textContent = text;
    if (statusTimer) clearTimeout(statusTimer);
    statusTimer = setTimeout(() => {
      status.textContent = '';
    }, 2500);
  };

  const flash = (marker: Element): void => {
    marker.classList.add('i18n-inspect-flash');
    setTimeout(() => marker.classList.remove('i18n-inspect-flash'), 1600);
  };

  const writeClipboard = (text: string): void => {
    const fallback = (): void => {
      const area = doc.createElement('textarea');
      area.value = text;
      area.setAttribute('readonly', '');
      area.style.position = 'fixed';
      area.style.opacity = '0';
      doc.body.appendChild(area);
      area.select();
      try {
        doc.execCommand('copy');
      } catch (err) {
        /* nothing else to try */
      }
      area.remove();
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).catch(fallback);
    } else {
      fallback();
    }
  };

  const copy = (marker: Element, what: 'id' | 'text'): void => {
    const text = what === 'id' ? config.getId(marker) : visibleText(marker);
    if (!text) return;
    writeClipboard(text);
    try {
      sessionStorage.setItem(LAST_COPIED_KEY, text);
    } catch (err) {
      /* storage unavailable: the readout still updates for this page */
    }
    flash(marker);
    setStatus(what === 'id' ? '\u2713 Copied the id' : '\u2713 Copied the text');
    if (config.onCopy) config.onCopy(text);
  };

  const domSwap = (on: boolean): void => {
    domEnglishOn = on;
    if (on) {
      const markers = doc.querySelectorAll(config.selector);
      for (let i = 0; i < markers.length; i++) {
        const m = markers[i];
        if (m.children.length > 0 || originals.has(m)) continue;
        const english = config.getEnglish(m);
        // Messages with placeholders or markup need runtime values to render; leave those as is.
        if (!english || /[{<]/.test(english)) continue;
        originals.set(m, m.textContent || '');
        m.textContent = english;
        m.setAttribute('data-i18n-showing-english', '');
      }
    } else {
      originals.forEach((text, m) => {
        m.textContent = text;
        m.removeAttribute('data-i18n-showing-english');
      });
      originals.clear();
    }
    if (config.onEnglishChange) config.onEnglishChange(on);
  };

  const toggleEnglish = (): void => {
    if (config.toggleEnglish) config.toggleEnglish();
    else domSwap(!domEnglishOn);
    // Re-render after the host has had a chance to update its state.
    setTimeout(() => {
      if (current && !pop.hidden) render(current);
    }, 0);
  };

  const position = (marker: Element): void => {
    const rect = marker.getBoundingClientRect();
    const gap = 6;
    const width = pop.offsetWidth;
    const height = pop.offsetHeight;
    let top = rect.bottom + gap;
    if (top + height > window.innerHeight - 8 && rect.top - gap - height > 8) top = rect.top - gap - height;
    let left = Math.min(rect.left, window.innerWidth - width - 8);
    if (left < 8) left = 8;
    pop.style.top = `${Math.max(8, top)}px`;
    pop.style.left = `${left}px`;
  };

  function render(marker: Element): void {
    pop.textContent = '';
    pop.appendChild(el('span', 'i18n-pop-label', config.idLabel));
    pop.appendChild(el('div', 'i18n-pop-id', config.getId(marker)));
    if (config.locale !== 'en') {
      pop.appendChild(el('span', 'i18n-pop-label', 'English original'));
      const english = config.getEnglish(marker);
      if (english === undefined) pop.appendChild(el('div', 'i18n-pop-muted', 'Loading…'));
      else if (english === null) pop.appendChild(el('div', 'i18n-pop-muted', 'Not available for this string'));
      else pop.appendChild(el('div', 'i18n-pop-en', english));
    }
    const actions = el('div', 'i18n-pop-actions');
    actions.appendChild(button('I', 'Copy id', () => copy(marker, 'id')));
    actions.appendChild(button('T', 'Copy text', () => copy(marker, 'text')));
    if (config.locale !== 'en') {
      const en = button('E', 'Show English', () => toggleEnglish());
      en.setAttribute('aria-pressed', englishOn() ? 'true' : 'false');
      actions.appendChild(en);
    }
    pop.appendChild(actions);
    pop.appendChild(el('div', 'i18n-pop-status'));
    pop.hidden = false;
    position(marker);
  }

  const show = (marker: Element): void => {
    current = marker;
    render(marker);
  };

  const hide = (): void => {
    current = null;
    pop.hidden = true;
  };

  const onOver = (e: Event): void => {
    const target = e.target as Element | null;
    if (!target || !target.closest) return;
    if (pop.contains(target)) {
      if (hideTimer) clearTimeout(hideTimer);
      return;
    }
    const marker = target.closest(config.selector);
    if (hideTimer) clearTimeout(hideTimer);
    if (showTimer) clearTimeout(showTimer);
    if (!marker || marker.closest('[data-i18n-inspector-ui]')) {
      if (current) hideTimer = setTimeout(hide, 250);
      return;
    }
    if (marker === current) return;
    // Instant when moving between strings with the card already open, a short delay otherwise so
    // sweeping the mouse across the page doesn't flicker cards everywhere.
    if (current) show(marker);
    else showTimer = setTimeout(() => show(marker), 150);
  };

  const isTyping = (target: EventTarget | null): boolean => {
    const t = target as HTMLElement | null;
    if (!t || !t.tagName) return false;
    const tag = t.tagName.toLowerCase();
    return tag === 'input' || tag === 'textarea' || tag === 'select' || t.isContentEditable;
  };

  const onKey = (e: KeyboardEvent): void => {
    if (e.ctrlKey || e.metaKey || e.altKey || e.repeat || isTyping(e.target)) return;
    const key = (e.key || '').toLowerCase();
    if (key === 'e' && config.locale !== 'en') {
      e.preventDefault();
      toggleEnglish();
      return;
    }
    if (!current) return;
    if (key === 'i' || key === 't') {
      e.preventDefault();
      copy(current, key === 'i' ? 'id' : 'text');
    } else if (key === 'escape') {
      hide();
    }
  };

  const onScroll = (): void => {
    if (current && !pop.hidden) {
      if (!current.isConnected) hide();
      else position(current);
    }
  };

  doc.addEventListener('mouseover', onOver, true);
  doc.addEventListener('keydown', onKey, true);
  window.addEventListener('scroll', onScroll, true);
  window.addEventListener('resize', onScroll);

  try {
    const last = sessionStorage.getItem(LAST_COPIED_KEY);
    if (last && config.onCopy) config.onCopy(last);
  } catch (err) {
    /* storage unavailable */
  }

  const dispose = (): void => {
    if (showTimer) clearTimeout(showTimer);
    if (hideTimer) clearTimeout(hideTimer);
    if (statusTimer) clearTimeout(statusTimer);
    if (domEnglishOn) domSwap(false);
    doc.removeEventListener('mouseover', onOver, true);
    doc.removeEventListener('keydown', onKey, true);
    window.removeEventListener('scroll', onScroll, true);
    window.removeEventListener('resize', onScroll);
    pop.remove();
    style.remove();
  };
  return { toggleEnglish, dispose };
}

/**
 * `installInspectorPopover` as inline-script source: `(${INSPECTOR_POPOVER_SOURCE})(config)`.
 * The `__name` shim neutralises esbuild's keepNames wrapper if a build ever turns it on.
 */
export const INSPECTOR_POPOVER_SOURCE = `(function(){var __name=function(f){return f;};return ${String(
  installInspectorPopover,
)};})()`;
